/**
 * SMS React Hooks
 * Epic 67: SMS Gateway & Kétirányú Protokoll
 *
 * TanStack Query hooks for SMS sending, history, and templates.
 */

import {
  createSmsTemplate,
  getSmsHistory,
  listSmsTemplates,
  sendSms,
  type SmsLogQueryResult,
  type SmsTemplateQueryResult,
} from '@/api/sms';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useSmsHistory(params?: { direction?: string; status?: string }) {
  const query = useQuery({
    queryKey: ['sms', 'history', params],
    queryFn: async () => {
      const res = await getSmsHistory(params);
      return res.data;
    },
  });

  return {
    result: query.data as SmsLogQueryResult | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useSmsTemplates() {
  const query = useQuery({
    queryKey: ['sms', 'templates'],
    queryFn: async () => {
      const res = await listSmsTemplates();
      return res.data;
    },
  });

  return {
    result: query.data as SmsTemplateQueryResult | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useSendSms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { phoneNumber: string; message: string }) => sendSms(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms', 'history'] });
    },
  });
}

export function useCreateSmsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      type: string;
      body: string;
      variables: string[];
      active: boolean;
    }) => createSmsTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms', 'templates'] });
    },
  });
}
